'use client';

import React from 'react';
import { Alert, AlertTitle, Box } from '@mui/material';

/**
 * Reusable inline alert component with consistent styling
 * @param {Object} props
 * @param {Object|null} props.result - Result object with success, message, title, error
 * @param {string} props.result.success - 'yes', 'no', 'warning', 'info'
 * @param {string} props.result.message - Alert message
 * @param {string} props.result.title - Optional alert title
 * @param {string} props.result.error - Alternative to message
 * @param {string} props.severity - Override severity: 'success', 'error', 'warning', 'info'
 * @param {React.ReactNode} props.children - Optional custom content
 * @param {React.ReactNode} props.action - Optional action button/element
 * @param {Object} props.sx - Additional MUI sx styles
 * @param {string|number} props.timeout - Fade timeout (default: 500)
 */
const InlineAlert = ({ 
  result, 
  severity, 
  children, 
  action, 
  sx = {}, 
  timeout = 500 
}) => {
  if (!result) return null;

  // Determine severity from result or prop
  const alertSeverity = severity || getSeverityFromResult(result);
  
  // Get title and message
  const title = result.title || getDefaultTitle(alertSeverity);
  const message = result.message || result.error || '';

  return (
    <Alert
        severity={alertSeverity}
        action={action}
        sx={{
          mt: 2,
          borderRadius: 2,
          border: `1px solid ${getBorderColor(alertSeverity)}`,
          '& .MuiAlert-message': {
            fontSize: '0.95rem',
            width: '100%',
          },
          ...sx,
        }}
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        <Box component="span">{children || message}</Box>
      </Alert>
  );
};

/**
 * Determine severity from result object
 */
const getSeverityFromResult = (result) => {
  if (result.success === 'yes') return 'success';
  if (result.success === 'no') return 'error';
  if (result.success === 'warning') return 'warning';
  return 'info';
};

/**
 * Get default title based on severity
 */
const getDefaultTitle = (severity) => {
  const titles = {
    success: '',
    error: 'Error',
    warning: 'Atención',
    info: 'Información',
  };
  return titles[severity] || '';
};

/**
 * Get border color based on severity
 */
const getBorderColor = (severity) => {
  const colors = {
    success: 'rgba(76, 175, 80, 0.2)',
    error: 'rgba(244, 67, 54, 0.2)',
    warning: 'rgba(255, 152, 0, 0.2)',
    info: 'rgba(33, 150, 243, 0.2)',
  };
  return colors[severity] || 'rgba(0, 0, 0, 0.1)';
};

export default InlineAlert;
